import 'jquery';
import 'popper.js';
import 'bootstrap/dist/js/bootstrap.min.js';
import React from 'react';
import { createRoot } from 'react-dom/client';
import DocumentTitle from 'react-document-title';
import { Provider, observer } from 'mobx-react';
import { unstable_HistoryRouter as HistoryRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { history, ScrollToTop, ErrorPage } from 'fn-common-ui';

import { utilsService } from './core/services/utils.service';
import { masterStore } from './core/stores/master.store.js';

import Header from './components/header/header.jsx';
import { Footer } from './components/footer/footer.jsx';
import JoyrideBase from './components/joyride-base/joyride-base';
import { PageAnnouncer } from './components/page-announcer/page-announcer';

import { AppEditPageWithRouter as AppEditPage } from './pages/app-edit.page.jsx';
import FeedbackPage from './pages/feedback.page.jsx';
import { FaqPage } from './pages/faq.page.jsx';
import { MyAppsPage } from './pages/my-apps.page.jsx';
import { NewApplicationPage } from './pages/new-application.page.jsx';

import config from 'config';
import '../styles/app.scss';

/* ATT Adobe: Part 3/3: Define docReady() callback to push events to DataManager
when their triggers are heard */
window.docReady = function () {
	
	//Disable auto page load pushEvent, to cut over to our custom pushEvents
	if (window.ddo) {		
		window.ddo.disableAutoPageLoad();
	}

	//Add a listener to send a pushEvent when an Adobe_Tag_Manager_Event is heard
	window.addEventListener('Adobe_Tag_Manager_Event', (event) => {			
		if (window.ddo) {
			window.ddo.pushEvent(event.detail.evtAction, event.detail.evtName, event.detail.params);
		}
	});

	// Fix to run pageLoad evt on page refresh
	window.addEventListener("load", function () {
		console.log('[Adobe Analytics] page reloaded, push ddo pageLoad : trying');
		if (window.ddo && localStorage.getItem('prevUrl') && localStorage.getItem('prevUrl') === this.location.href) {
			console.log('[Adobe Analytics] page reloaded, push ddo pageLoad : calling');
		}
		localStorage.removeItem('prevUrl');		
	});

	window.addEventListener('beforeunload', function () {
		localStorage.setItem('prevUrl', location.href);
	});
}

// Trigger a Adobe_Tag_Manager_Event on route change
history.listen(({location}) => {
	const params = {
		'page.location.url': location.pathname.replace(/\s/g, '') //get rid of whitespace
	}
	utilsService.triggerCustomEvent('Adobe_Tag_Manager_Event', {
		evtAction: 'pageLoad',
		evtName: 'Page_Display',
		params
	});
});

// React Router 6 wrapper to pass params as props to ErrorPage
function ErrorPageWithParams(props) {
	const params = useParams();
	return <ErrorPage {...props} cause={params.cause} />;
}

@observer class App extends React.Component {

	constructor(props) {
		super(props);
		//check for URL token parameter
		const urlToken = utilsService.getUrlParameter('token');
		if (urlToken && urlToken.length > 0) {
			masterStore.userStore.initUserObject(urlToken);
		} else {
			//get token the long way; first time logging in
			masterStore.userStore.validateUser();
		}
		document.addEventListener('keydown', function (e) {
			if (e.keyCode === 9) {
				jQuery('body').addClass('show-focus-syles');
			}
		});

		document.addEventListener('click', function () {
			jQuery('body').removeClass('show-focus-syles');
		});
	}

	componentDidMount() {
		/* ATT Adobe: Part 2/3: Add footer script as late in lifecylcle as possible,
		which will call docReady() when DataManager is available */
		this.loadAdobeFooterScript();
	}

	loadAdobeFooterScript = (retries = 10) => {
		if (typeof window.detmScriptLoaderConfig !== 'undefined') {
			const script = document.createElement('script');
			script.src = 'https://www.att.com/scripts/adobe/stage/detm-container-ftr.js';
			try {
				document.body.appendChild(script);
			} catch (e) {
				console.error('Error appending adobe footer script', e);
			}
		} else if (retries > 0) {
			setTimeout(() => this.loadAdobeFooterScript(retries - 1), 500);
		} else {
			console.warn('Adobe header script did not initialize detmScriptLoaderConfig in time');
		}
	}

	handleSkipNav = () => {
		//Fix for FPSE-1393 and FPSE-1394. Skip Navigation is not working on IE, as elements without a tabIndex don't take focus. For 508 compliance, #main-content can't permanently have a tabIndex.
		const mainContent = jQuery('#main-content');
		mainContent.attr('tabindex', -1);
		mainContent.focus();
		mainContent.blur(() => {
			mainContent.removeAttr('tabindex');
		});
	}

	applicationContent = () => {
		//config.showOnboardingWalkthrough = false;
		return (
			<div>
				{config.showOnboardingWalkthrough &&
					<JoyrideBase
						location={location.pathname}
						additionalStepActions={masterStore.myAppsStore.handleJoyrideStep.bind(masterStore.myAppsStore)}
						joyrideStore={masterStore.joyrideStore} />
				}
				<ScrollToTop path={location.pathname} finishGoingToError={masterStore.newAppMasterStore.finishGoingToError} isGoingToError={masterStore.newAppMasterStore.isGoingToError}>
					<DocumentTitle title="FirstNet App Control">
						<main>
							<a href="#main-content" className="skipnav" onClick={this.handleSkipNav}>Skip Navigation</a>
							<Header store={masterStore} />
							<section id="main-content">
								<Routes>
									<Route path="/" element={<MyAppsPage />} />
									<Route path="/my-apps" element={<MyAppsPage />} />
									<Route path="/faq" element={<FaqPage />} />
									<Route path="/contact" element={<FeedbackPage />} />
									<Route path="/app/:mode/:appId/:appVersion/:saveId?" element={<AppEditPage />} />
									<Route path="/new-application" element={<Navigate to="/new-application/app-details/" replace />} />
									<Route path="/new-application/:section/:saveId?" element={<NewApplicationPage />} />
									<Route path="*" element={<Navigate to="/error/404" replace />} />
								</Routes>
							</section>
							<Footer store={masterStore} />
						</main>
					</DocumentTitle>
				</ScrollToTop>
			</div>
		);
	}

	errorContent = () => {
		return (
			<React.Fragment>
				<main>
					<a href="#main-content" className="skipnav" onClick={this.handleSkipNav}>Skip Navigation</a>
					<Header store={masterStore} />
					<section id="main-content">
						<Routes>
							<Route path="/error/:cause" element={<ErrorPageWithParams config={config} />} />
							<Route path="/error" element={<ErrorPage />} />
							<Route path="*" element={<Navigate to="/error/404" replace />} />
						</Routes>
					</section>
					<Footer store={masterStore} />
				</main>
			</React.Fragment>
		)
	}

	getUnauthorizedError = () => {
		return <ErrorPage
			config={config}
			currentPortal="App Control"
			userRoles={masterStore.userStore.user.roles}
			logoutUser={masterStore.userStore.logoutUser}
			cause="unauthorized" />
	}

	sessionDependentContent() {
		return masterStore.userStore.userValidationDone ? (
			masterStore.isLoggedIn ? (
				<Routes>
					<Route path="/error/unauthorized" element={this.getUnauthorizedError()} />
					<Route path="/error/*" element={this.errorContent()} />
					<Route path="*" element={this.applicationContent()} />
				</Routes>
			) : (
					this.getUnauthorizedError()
				)
		) : (
				<main id="main-content">
					<div className="fn-loading">
						<div className="fn-loading-logo">
							<img src="/images/firstnet-logo.svg" width="173" height="51" alt="FirstNet logo" />
						</div>
						<h1 className="fn-loading-text">Loading</h1>
						<div className="fn-loading-circle">
							<div className="c1 c"></div>
							<div className="c2 c"></div>
							<div className="c3 c"></div>
							<div className="c4 c"></div>
							<div className="c5 c"></div>
							<div className="c6 c"></div>
							<div className="c7 c"></div>
							<div className="c8 c"></div>
							<div className="c9 c"></div>
							<div className="c10 c"></div>
							<div className="c11 c"></div>
							<div className="c12 c"></div>
						</div>
					</div>
				</main>
			);
	}

	render() {
		return (
			<HistoryRouter history={history}>
				<Provider store={masterStore} user={masterStore.getUser()}>
					<React.Fragment>
						<PageAnnouncer />
						{this.sessionDependentContent()}
					</React.Fragment>
				</Provider>
			</HistoryRouter>
		);
	}

}

const root = createRoot(document.getElementById('app'));
root.render(<App />);
